'use strict';

import moment from 'moment';

import nutrKeys from '../tables/nutrient-order';
import allNutrients from '../tables/nutrients';
import allTags from '../tables/tags';

import { roundForHumans } from './Math';

import allRecommendations from '../tables/recommendations';
import { getMealTypeInfoByName } from './Meals';

export function addNutrientSet(sets) {
    const nutrients = {};

    sets.forEach(set => {
        Object.keys(set).forEach(nutrNo => {
            nutrients[nutrNo] = nutrients[nutrNo] || 0;
            nutrients[nutrNo] += set[nutrNo];
        });
    });

    return nutrients;
}

export function factorEnvelope(envelope, factor = 1) {
    const ret = {};

    Object.keys(envelope).forEach(nutrNo => {
        const range = envelope[nutrNo];

        ret[nutrNo] = ret[nutrNo] || {};

        if (typeof range.min === 'number') {
            ret[nutrNo].min = range.min / factor;
        }

        if (typeof range.max === 'number') {
            ret[nutrNo].max = range.max / factor;
        }
    });

    return ret;
}

export function getNutrientsForMeals(meals, contents, defaultPortion = 1, defaultValue = 0, roundValues = false) {
    const nutrients = {};

    meals.forEach(meal => {
        let content = null, contribs = {};
        let portion = meal.logged_amount || (meal.scaling || 1) * (defaultPortion || 1);

        if (meal.deleted) {
            return;
        }

        if (meal.recipe_uuid) content = contents[meal.recipe_uuid];
        if (meal.food_uuid) content = contents[meal.food_uuid];

        // Only enter the gram path when the meal was actually logged in grams. A bare
        // `logged_grams === 0` check would trap serving-unit logs whose gram/ml fields
        // defaulted to 0 — that combined with a non-zero `grams_per_serving` would
        // multiply every nutrient by 0 and silently zero out the meal's nutrition.
        const isGramLog = meal.logged_grams > 0
            || (meal.logged_grams === 0 && (meal.logged_unit === 'g' || meal.logged_unit === 'oz'));
        const isMlLog = meal.logged_milliliters > 0
            || (meal.logged_milliliters === 0 && (meal.logged_unit === 'ml' || meal.logged_unit === 'fl oz'));

        if (isGramLog && content && content.grams_per_serving && content.nutrients && content.nutrients.values) {
            // User explicitly logged a mass for a recipe or food thats measured in grams.
            Object.keys(content.nutrients.values).forEach(nutrNo => {
                contribs[nutrNo] = (content.nutrients.available[nutrNo] || content.nutrients.values[nutrNo])
                                 ? content.nutrients.values[nutrNo] / content.grams_per_serving * meal.logged_grams
                                 : null;

                if (roundValues) {
                    contribs[nutrNo] = Math.round(contribs[nutrNo]);
                }
            });
        } else if (isMlLog && content && content.milliliters_per_serving && content.nutrients && content.nutrients.values) {
            // User explicitly logged a fixed volume of a recipe or food that is measured in volume
            Object.keys(content.nutrients.values).forEach(nutrNo => {
                contribs[nutrNo] = (content.nutrients.available[nutrNo] || content.nutrients.values[nutrNo])
                                 ? content.nutrients.values[nutrNo] / content.milliliters_per_serving * meal.logged_milliliters
                                 : null;
                if (roundValues) {
                    contribs[nutrNo] = Math.round(contribs[nutrNo]);
                }
            });
        } else if ((content && content.nutrients && content.nutrients.values)) {
            // User only ate a portion of a recipe or food multiple it's nutrition by the portion size
            Object.keys(content.nutrients.values).forEach(nutrNo => {
                contribs[nutrNo] = (content.nutrients.available[nutrNo] || content.nutrients.values[nutrNo])
                                 ? content.nutrients.values[nutrNo] * portion
                                 : null;
                if (roundValues) {
                    contribs[nutrNo] = Math.round(contribs[nutrNo]);
                }
            });
        }

        if (meal.water_intake_l > 0) {
            if (!contribs['FLU']) {
                contribs['FLU'] = 0
            }

            contribs['FLU'] += meal.water_intake_l * 1000;
        }

        Object.keys(contribs).forEach(nutrNo => {
            let nutrientValue = contribs[nutrNo] == null ? defaultValue : contribs[nutrNo];

            if (nutrientValue !== null && typeof nutrientValue !== 'undefined' && !isNaN(nutrientValue)) {
                nutrients[nutrNo] = nutrients[nutrNo] || 0;
                nutrients[nutrNo] = nutrients[nutrNo] + nutrientValue;
            }
        });
    });

    return nutrients;
}

export function getNutrientsForRecipes(recipes, factor = 1) {
    const nutrients = {};

    recipes.forEach(recipe => {
        if (!(recipe && recipe.nutrients && recipe.nutrients.values)) {
            return;
        }

        Object.keys(recipe.nutrients.values).forEach(nutrNo => {
            nutrients[nutrNo] = nutrients[nutrNo] || 0;
            nutrients[nutrNo] += (recipe.nutrients.values[nutrNo] || 0) * factor;
        });
    });

    return nutrients;
}

export function getNutrientsForIngredients(ingredients, contents, factor = 1) {
    const nutrients = {};

    if (!Array.isArray(ingredients)) {
        ingredients = [ingredients];
    }

    ingredients.forEach(ingredient => {
        let content = null;

        if (ingredient.recipe) content = contents[ingredient.recipe.uuid];
        if (ingredient.food) content = contents[ingredient.food.uuid];

        if (!(content && content.nutrients && content.nutrients.values)) {
            return;
        }

        Object.keys(content.nutrients.values).forEach(nutrNo => {
            nutrients[nutrNo] = nutrients[nutrNo] || 0;
            nutrients[nutrNo] += (content.nutrients.values[nutrNo] || 0) * factor;
        });
    });

    return nutrients;
}

export function convertAgeToBracket(age) {
    // the second comparison is backwards because forwards it messes up the syntax highlighting in Sublime Text.
    if (age.years < 1 && 6 > age.months) {
        return '0-6-mo';
    }

    if (age.years < 1 && age.months >= 6) {
        return '6-12-mo';
    }

    if (age.years >= 1 && age.years <= 3) {
        return '1-3-yr';
    }

    if (age.years >= 4 && age.years <= 8) {
        return '4-8-yr';
    }

    if (age.years >= 9 && age.years <= 13) {
        return '9-13-yr';
    }

    if (age.years >= 14 && age.years <= 18) {
        return '14-18-yr';
    }

    if (age.years >= 19 && age.years <= 30) {
        return '19-30-yr';
    }

    if (age.years >= 31 && age.years <= 50) {
        return '31-50-yr';
    }

    if (age.years >= 51 && age.years <= 70) {
        return '51-70-yr';
    }

    if (age.years > 70) {
        return '70+yr';
    }

    return false;
}

export function getDemographic(user) {
    if (!user) {
        return 'all-users';
    }

    if (!user.birthdate && !user.gender) {
        return 'all-users';
    }

    // First part is gender. There's also subcategories for females
    var gender = (user.gender || 'female'),
        age = {
            years: 30,
            months: 0,
        };

    if (user.birthdate) {
        age = {
            years: moment().diff(moment(user.birthdate), 'years'),
            months: moment().diff(moment(user.birthdate), 'months') % 12,
        };
    }

    // Only append the pregnant or lactating demographics if the user
    // is both female and within the correct age range
    if (user.gender == 'female' && age.years >= 14 && age.years <= 50) {
        if (user.pregnant) {
            gender += '-pregnant';
        }

        if (user.lactating) {
            gender += '-lactating';
        }
    }

    if (false === (age = convertAgeToBracket(age))) {
        return 'all-ages';
    }

    return [gender, age].join('-');
}

let _distributions = null;

export function resetDistributionMultipliers() {
    // Loops through every nutrient and if it has a distribution array, randomly
    // pick one of them to be the distribution for this days worth of meals.
    _distributions = {};

    Object.keys(allNutrients).forEach(nutrNo => {
        const nutrient = allNutrients[nutrNo];

        if (!nutrient.distributions) {
            return;
        }

        // Set and remember a nutrient distribution.
        _distributions[nutrNo] = nutrient.distributions[Math.floor(Math.random() * nutrient.distributions.length)];
    });
}

export function getDistributionMultiplier(nutrNo, field, mealType) {
    const defaults = {
        'min'  : {'Breakfast': .15, 'Lunch': .20, 'Dinner': .25},
        'ideal': {'Breakfast': .25, 'Lunch': .30, 'Dinner': .35, 'Snack': .10},
        'max'  : {'Breakfast': .35, 'Lunch': .35, 'Dinner': .40, 'Snack': .15},
    };

    if (_distributions === null) {
        resetDistributionMultipliers();
    }

    if (_distributions[nutrNo] &&
        _distributions[nutrNo][field] &&
        typeof _distributions[nutrNo][field][mealType] !== 'undefined') {

        return _distributions[nutrNo][field][mealType];
    }

    return defaults[field] && defaults[field][mealType] || 0;
}


/*
Cross-product formula for determining distributions for standard and non-standard meal type lists.

Order: Breakfast, Lunch, Dinner, Snack

Our standard distribution expects there to be Breakfast, Lunch and Dinner with a snack cleanup function.
Breakfast + Lunch + Dinner = 100

If our user is using a non-standard pattern, say, Breakfast, Breakfast, Dinner (because why not?)
then we need to return different multipliers so that the two breakfasts amd one dinner will end up getting
close to your daily totals. They'll be bigger breakfasts.

We can use this to renormalize the non-standard patterns magnitude to match our standard pattern magnitude.

For Example, let's assume the min values, which add up to 60 for our standard distribution.
Our new pattern, Breakfast Breakfast Dinner adds up to 55, so we multiply each of our standard distribution
numbers by 60 / 55. We'll see that breakfast + breakfast + dinner adds up to the same magnitude as breakfast +
lunch + dinner of our standard distribution.

Breakfast      + Lunch          + Dinner         = 100
15             + 20             + 25             = 60

Breakfast      + Breakfast      + Dinner         = 100
15             + 15             + 25             = 55
15 * (60 / 55) + 15 * (60 / 55) + 25 * (60 / 55) = 60

Another pattern, breakfast breakfast breakfast (because breakfast food is awesome), gives us a normalizing
factor of 60 / 45 instead.

Breakfast      + Breakfast      + Breakfast      = 100
15             + 15             + 15             = 45
15 * (60 / 45) + 15 * (60 / 45) + 15 * (60 / 45) = 60

*/

/**
 * Generates a straw-man prescription for a given meal type from an
 * all day prescription. This function has side effects.
 */
export function computeDefaultMealRx(allDayRx, mealType, precise = false) {
    const rx = {
        meal_type: mealType,
        envelope: {},
        implicit: true,
    };

    Object.keys(allDayRx).forEach(nutrNo => {
        rx.envelope[nutrNo] = {};

        const range = allDayRx[nutrNo];
        let mult;

        if (typeof range.min !== 'undefined') {
            mult = getDistributionMultiplier(nutrNo, 'min', mealType);
            const min = precise ? range.min * mult : roundForHumans(range.min * mult);
            if (min > 0) {
                rx.envelope[nutrNo].min = min;
            }
        }

        if (typeof range.max !== 'undefined') {
            mult = getDistributionMultiplier(nutrNo, 'max', mealType);
            rx.envelope[nutrNo].max = precise ? range.max * mult : roundForHumans(range.max * mult);
        }
    });

    return rx;
}

/**
 * Resolves the effective min/max for one envelope nutrient range.
 * A range can carry direct min/max values, implicit.min/implicit.max values, or a mix.
 * The direct value wins; the implicit value fills the gap.
 *
 * @param  {Object} range One nutrient entry from an envelope, e.g. {min, max, implicit: {min, max}}
 * @return {Object}       {min, max}; either can be undefined
 */
export function getEnvelopeMinMax(range) {
    if (!range) {
        return { min: undefined, max: undefined };
    }

    let min = range.min;
    let max = range.max;

    if (typeof min !== 'number' && range.implicit && typeof range.implicit.min === 'number') {
        min = range.implicit.min;
    }
    if (typeof max !== 'number' && range.implicit && typeof range.implicit.max === 'number') {
        max = range.implicit.max;
    }

    return { min, max };
}

export function convertEnvelopeToFilters(envelope, filters = {}, minMult = 1, factor = 1) {
    Object.keys(envelope).forEach(nutrNo => {

        if (!(allNutrients[nutrNo] && allNutrients[nutrNo].Filter)) {
            return;
        }

        const fieldName = allNutrients[nutrNo].Filter
        const { min, max } = getEnvelopeMinMax(envelope[nutrNo]);

        if (typeof min === 'number' && typeof max === 'number') {
            filters[fieldName] = {
                'gte': Math.round(min * minMult * 100) / 100 / factor,
                'lte': Math.round(max * 100) / 100 / factor,
            };
        } else if (typeof min !== 'number' && typeof max === 'number') {
            filters[fieldName] = {'lte': Math.round(max * 100) / 100 / factor};
        } else if (typeof min === 'number' && typeof max !== 'number') {
            filters[fieldName] = {'gte': Math.round(min * minMult * 100) / 100 / factor};
        }
    });

    return filters;
}

export function getIdealsFromEnvelope(envelope, factor = 1) {
    const ideals = {};

    Object.keys(envelope).forEach(nutrNo => {
        const { min, max } = getEnvelopeMinMax(envelope[nutrNo]);

        if (typeof min === 'number' && typeof max !== 'number') {
            ideals[nutrNo] = min / factor;
        } else if (typeof min !== 'number' && typeof max === 'number') {
            ideals[nutrNo] = max * 0.5 / factor;
        } else if (typeof min === 'number' && typeof max === 'number') {
            ideals[nutrNo] = (min + max) / 2 / factor;
        }
    });

    return ideals;
}

export function getNeededNutrients(nutrients, envelope) {
    const needed = {};

    Object.keys(envelope).forEach(nutrNo => {
        const range = envelope[nutrNo];
        const value = nutrients[nutrNo] || 0;

        // There is insufficient nutrient in the meal
        if (typeof range.min === 'number' && typeof range.max === 'number' && value < range.min) {
            needed[nutrNo] = {
                'min': range.min - value,
                'max': range.max - value,
                'orig': value,
            };
            return;
        }

        // There is insufficient nutrient in the meal
        if (typeof range.min === 'number' && typeof range.max !== 'number' && value < range.min) {
            needed[nutrNo] = {
                'min': range.min - value,
                'orig': value,
            };
            return;
        }
    });

    if (!Object.keys(needed).length) {
        return needed;
    }

    // Loop again, making sure we don't bust maximums as well
    Object.keys(envelope).forEach(nutrNo => {
        const range = envelope[nutrNo];
        const value = nutrients[nutrNo] || 0;

        // Now make sure we limit the amount of any unneeded sources so as to not exceed
        // our maximum values
        if (typeof range.max === 'number' && typeof range.min === 'number' && value < range.min) {
            needed[nutrNo] = {
                'min': range.min - value,
                'max': range.max - value,
                'orig': value,
            };
            return;
        }

        if (typeof range.max === 'number' && typeof range.min === 'number' && value > range.min) {
            needed[nutrNo] = {
                //'min': range.min - value,
                'max': range.max - value,
                'orig': value,
            };
            return;
        }

        if (typeof range.max === 'number' && typeof range.min !== 'number') {
            needed[nutrNo] = {
                'max': range.max - value,
                'orig': value,
            };
            return;
        }
    });
    return needed;
}

export function addDefaultAddSwapTags({mealType, profile, meals, modalSettings, contents, plan = null}) {
    const { avoidances = [], exclude_foods = [], equipment = [], diets = [], limit_tags = [], skill_level } = profile.preferences || {};
    const mealTypeInfo = getMealTypeInfoByName(mealType, null, plan?.meal_types || profile?.preferences?.meal_types);
    
    // Add the users taste profile settings
    modalSettings.extraFilters = modalSettings.extraFilters || {};
    modalSettings.extraFilters['!foods'] = exclude_foods;
    modalSettings.defaultTags   = (modalSettings.defaultTags || []).concat(limit_tags);
    modalSettings.defaultAvoids = (modalSettings.defaultAvoids || []).concat(avoidances);
    modalSettings.defaultExcludes = (modalSettings.defaultExcludes || []).concat(allTags.equipment.tags.filter(e => !equipment.includes(e)));

    const alreadyInMeal = meals.filter(m => m.meal == mealTypeInfo.name);

    let hasMain = false,
        hasSide = false;

    // Figure out if we have a main dish and a side dish already
    hasMain = alreadyInMeal.find(item => !item.side_dish);
    hasSide = alreadyInMeal.find(item => item.side_dish);

    if (skill_level == 'Beginner') {
        modalSettings.defaultExcludes.push('Intermediate');
        modalSettings.defaultExcludes.push('Advanced');
    } else if (skill_level == 'Intermediate') {
        modalSettings.defaultExcludes.push('Advanced');
    }

    if (mealTypeInfo.main_dish === 'Snack') {
        modalSettings.defaultTags.push(mealTypeInfo.main_dish);
    }

    if (mealTypeInfo.main_dish === 'Breakfast') {
        // Is there a main dish already?
        if (hasMain && hasSide) {
            // Just add the lunch tag.
            modalSettings.defaultTags.push(mealTypeInfo.main_dish);
        } else if (!hasMain && hasSide) {
            modalSettings.defaultTags.push(mealTypeInfo.main_dish);
        } else if (hasMain && !hasSide) {
            modalSettings.defaultTags.push(mealTypeInfo.side_dish);
        } else {
            modalSettings.defaultTags.push(mealTypeInfo.main_dish);
        }
    }

    if (mealTypeInfo.main_dish === 'Lunch') {
        // Is there a main dish already?
        if (hasMain && hasSide) {
            // Just add the lunch tag.
            modalSettings.defaultTags.push(mealTypeInfo.main_dish);
        } else if (!hasMain && hasSide) {
            modalSettings.defaultTags.push(mealTypeInfo.main_dish);
        } else if (hasMain && !hasSide) {
            modalSettings.defaultTags.push(mealTypeInfo.side_dish);
        } else {
            modalSettings.defaultTags.push(mealTypeInfo.main_dish);
        }
    }

    if (mealTypeInfo.main_dish === 'Dinner') {
        // Is there a main dish already?
        if (hasMain && hasSide) {
            // Don't add any tags
        } else if (!hasMain && hasSide) {
            modalSettings.defaultTags.push(mealTypeInfo.main_dish);
        } else if (hasMain && !hasSide) {
            // Do we have a meal kit or ready made meal? Use a different tag.
            if (hasMain.meal_type === 'food' && contents[hasMain.food_uuid] && contents[hasMain.food_uuid].product_type === 'Meal Kit') {
                modalSettings.defaultTags.push('Dinner Meal Kit Side');
            } else if (hasMain.meal_type === 'food' && contents[hasMain.food_uuid] && contents[hasMain.food_uuid].product_type === 'Ready Made Meal') {
                modalSettings.defaultTags.push('Dinner Ready Made Side');
            } else {
                modalSettings.defaultTags.push(mealTypeInfo.side_dish);
            }
        } else {
            modalSettings.defaultTags.push(mealTypeInfo.main_dish);
        }
    }
}

export function limitEnvelopeByMaximums(inside, outside) {
    Object.keys(outside).forEach(nutrNo => {
        let range = outside[nutrNo];

        if (!(range && range.max)) {
            return;
        }

        inside[nutrNo] = inside[nutrNo] || {};

        // We need to constrain needed's max. If needed[nutrNo]['max'] > explicitRx[nutrNo]['max']
        // then we need to set needed[nutrNo]['max'] to the lower value.
        if (!inside[nutrNo].max) {
            inside[nutrNo].max = range.max;
        } else if (inside[nutrNo].max > range.max) {
            inside[nutrNo].max = range.max;
        }
    });

    return inside;
}

/**
 * Big Rotten Function that does a lot of the work of the Add/Swap interface.
 *
 * This function computes search parameters to:
 * 1. Meet the nutrition of the profile
 * @param {[type]} options.mealType      [description]
 * @param {[type]} options.excludeUuids  [description]
 * @param {[type]} options.meals         [description] 
 * @param {[type]} options.recipes       [description]
 * @param {[type]} options.modalSettings [description]
 */
export function addDefaultAddSwapFilters({profile, mealType, excludeUuids = [], meals = [], contents = {}, modalSettings = {}, plan = {}}) {
    const allDayRx = profile.preferences.daily_totals;
    const mealTypeInfo = getMealTypeInfoByName(mealType, null, plan?.meal_types || profile?.preferences?.meal_types);

    // Add the skipped content to the exclusions
    if (excludeUuids) {
        modalSettings.extraFilters['!uuid'] = modalSettings.extraFilters['!uuid'] || [];
        modalSettings.extraFilters['!uuid'] = modalSettings.extraFilters['!uuid'].concat(excludeUuids);

        modalSettings.extraFilters['!main_dish'] = modalSettings.extraFilters['!main_dish'] || [];
        modalSettings.extraFilters['!main_dish'] = modalSettings.extraFilters['!main_dish'].concat(excludeUuids);

        modalSettings.extraFilters['!side_dish'] = modalSettings.extraFilters['!side_dish'] || [];
        modalSettings.extraFilters['!side_dish'] = modalSettings.extraFilters['!side_dish'].concat(excludeUuids);
    }

    // If the profile is pregnant, avoid the NOPREG tag.
    if (profile.pregnant) {
        modalSettings.defaultExcludes = modalSettings.defaultExcludes || [];
        modalSettings.defaultExcludes.push('NOPREG');
    }

    // If the profile has a birthdate, and is 12 or under, add the kid friendly tag, otherwise exclude the toddler tag
    if (profile.birthdate) {
        const age = moment().diff(profile.birthdate, 'year');
        if (age > 12) {
            modalSettings.defaultExcludes = modalSettings.defaultExcludes || [];
            modalSettings.defaultExcludes.push('Toddler');
        } else {
            modalSettings.defaultTags.push('Kid Friendly');
        }
    }
    
    // Make sure that we're not going to exceed the patients prescription for this meal
    const rx = mealTypeInfo?.envelope;

    // User does not have a prescription - don't limit.
    if (!rx) {
        return modalSettings;
    }

    // Add up some useful nutrient information
    const mealTypeName = mealTypeInfo.name;
    const mealNutrients = getNutrientsForMeals(meals.filter(m => m.meal === mealTypeName), contents, profile.portion);

    // Create a convenience envelope we can manipulate. We don't
    // want to change the original because that'll cause some seriously
    // unwanted and confusing side-effects for the user.
    const envelope = JSON.parse(JSON.stringify(rx)); // deep copy the envelope
    const hasMainDish = [mealTypeInfo.main_dish];

    // What other meals do we have in this day?
    const sameDayMeals = meals.filter(item => (
        !item.deleted &&
        item.meal !== mealTypeInfo.name
    ));

    // Count the meal types we've currently got.
    meals.forEach((m) => {
        const mealTypeInfo = profile.preferences.meal_types.find((mt) => mt.name === m.meal) || {};

        if (!hasMainDish.includes(mealTypeInfo.main_dish)) {
            hasMainDish.push(mealTypeInfo.main_dish);
        }
    });

    // Subtract any content still in this meal from the envelope first
    Object.keys(envelope).forEach(nutrNo => {
        const range = envelope[nutrNo];
        const planned = mealNutrients[nutrNo] || 0;

        if (!planned) {
            return;
        }

        // Handle both direct min/max and implicit.min/implicit.max
        let min = range.min;
        let max = range.max;
        let useImplicit = false;

        // If no direct min/max, check for implicit values
        if (typeof min !== 'number' && range.implicit && typeof range.implicit.min === 'number') {
            min = range.implicit.min;
            useImplicit = true;
        }
        if (typeof max !== 'number' && range.implicit && typeof range.implicit.max === 'number') {
            max = range.implicit.max;
            useImplicit = true;
        }

        // Subtract planned nutrition from the available envelope
        if (typeof min === 'number' && typeof max !== 'number') {
            min = min - planned;
        } else if (typeof min !== 'number' && typeof max === 'number') {
            max = max - planned;
        } else if (typeof min === 'number' && typeof max === 'number') {
            min = min - planned;
            max = max - planned;
        }

        // Clean up negative values and update the range
        if (typeof min === 'number' && min < 0) {
            min = undefined;
        }
        if (typeof max === 'number' && max < 0) {
            max = undefined;
        }

        // Update the envelope with the new values
        if (useImplicit) {
            // Ensure implicit object exists
            if (!range.implicit) {
                range.implicit = {};
            }

            // Update implicit values
            if (typeof min === 'number') {
                range.implicit.min = min;
            } else if (min === undefined && range.implicit.min !== undefined) {
                delete range.implicit.min;
            }

            if (typeof max === 'number') {
                range.implicit.max = max;
            } else if (max === undefined && range.implicit.max !== undefined) {
                delete range.implicit.max;
            }
        } else {
            // Update direct values
            if (typeof min === 'number') {
                range.min = min;
            } else if (min === undefined && range.min !== undefined) {
                delete range.min;
            }

            if (typeof max === 'number') {
                range.max = max;
            } else if (max === undefined && range.max !== undefined) {
                delete range.max;
            }
        }

        envelope[nutrNo] = range;
    });

    // If we're not adding our last meal, use the per-meal envelope nutrition constraints
    if (!(hasMainDish.includes('Breakfast') &&
          hasMainDish.includes('Lunch') &&
          hasMainDish.includes('Dinner') &&
          hasMainDish.includes('Snack'))) {

        modalSettings.extraFilters = convertEnvelopeToFilters(envelope, modalSettings.extraFilters, 1, profile.portion);

        // Try to find the center of the envelope
        // modalSettings.envelope = envelope;
        modalSettings.ideals = getIdealsFromEnvelope(envelope, profile.portion);

        return modalSettings;
    }

    // We need to make sure we don't bust through the daily totals with our pick. So the meal selection has to match
    // both the per-meal prescription and, in combination with the other meals planned for the day, the all day
    // prescription.
    const sameDayNutrients = getNutrientsForMeals(sameDayMeals, contents, profile.portion);

    // If we're picking the last meal, we need to make sure we get enough of what's left
    // AND we need to make sure we don't go over on anything either.
    Object.keys(envelope).forEach(nutrNo => {
        envelope[nutrNo] = envelope[nutrNo] || {}

        const allDayRange = allDayRx[nutrNo];
        const rxRange = envelope[nutrNo];

        // If we're adding, include the meals already in the meal slot (mealNutrients)
        // If we're swapping, don't include the meals already in the slot, they'll get replaced
        const consumed = (sameDayNutrients[nutrNo] || 0) + (mealNutrients[nutrNo] || 0);

        if (!allDayRange) {
            return;
        }

        // Handle both direct min/max and implicit.min/implicit.max for daily constraints
        let allDayMin = allDayRange.min;
        let allDayMax = allDayRange.max;
        let rxMin = rxRange.min;
        let rxMax = rxRange.max;
        let useImplicitAllDay = false;
        let useImplicitRx = false;

        // If no direct allDay min/max, check for implicit values
        if (typeof allDayMin !== 'number' && allDayRange.implicit && typeof allDayRange.implicit.min === 'number') {
            allDayMin = allDayRange.implicit.min;
            useImplicitAllDay = true;
        }
        if (typeof allDayMax !== 'number' && allDayRange.implicit && typeof allDayRange.implicit.max === 'number') {
            allDayMax = allDayRange.implicit.max;
            useImplicitAllDay = true;
        }

        // If no direct rx min/max, check for implicit values
        if (typeof rxMin !== 'number' && rxRange.implicit && typeof rxRange.implicit.min === 'number') {
            rxMin = rxRange.implicit.min;
            useImplicitRx = true;
        }
        if (typeof rxMax !== 'number' && rxRange.implicit && typeof rxRange.implicit.max === 'number') {
            rxMax = rxRange.implicit.max;
            useImplicitRx = true;
        }

        // Since we're the last meal, make sure we get enough of stuff.
        if (typeof allDayMin === 'number' && typeof rxMin === 'number') {
            let remainingNeeded = allDayMin - consumed;

            if (rxMin < remainingNeeded) {
                // Update the appropriate value (implicit or direct)
                if (useImplicitRx) {
                    if (!rxRange.implicit) rxRange.implicit = {};
                    rxRange.implicit.min = remainingNeeded;
                } else {
                    rxRange.min = remainingNeeded;
                }
            }

            if (rxMin <= 0) {
                if (useImplicitRx && rxRange.implicit) {
                    delete rxRange.implicit.min;
                } else {
                    delete rxRange.min;
                }
            }
        }

        // But not TOO much stuff...
        if (typeof allDayMax === 'number' && typeof rxMax === 'number') {
            let maxRemaining = allDayMax - consumed;

            if (rxMax > maxRemaining) {
                // Update the appropriate value (implicit or direct)
                if (useImplicitRx) {
                    if (!rxRange.implicit) rxRange.implicit = {};
                    rxRange.implicit.max = maxRemaining;
                } else {
                    rxRange.max = maxRemaining;
                }
            }

            if (maxRemaining < 0) {
                if (useImplicitRx) {
                    if (!rxRange.implicit) rxRange.implicit = {};
                    rxRange.implicit.max = 0;
                } else {
                    rxRange.max = 0;
                }
            }
        }
    });

    // Convert the envelope to a search filters
    modalSettings.extraFilters = convertEnvelopeToFilters(envelope, modalSettings.extraFilters, 1, profile.portion);

    // Try to find the center of the envelope
    // modalSettings.envelope = envelope;
    modalSettings.ideals = getIdealsFromEnvelope(envelope, profile.portion);
}

/**
 * Returns the full nutrition envelope for restaurant searches.
 * When superLenient is true, removes the sodium, saturated fat, and sugar limits.
 *
 * @param  {Object}  envelope       The meal nutrition envelope
 * @param  {Object}  allDayEnvelope Unused; kept for the callers
 * @param  {Boolean} superLenient   When true, remove sodium (307), saturated fat (606), added sugar (ASG), and total sugar (269)
 * @return {Object}                 The nutrition envelope
 */
export function transformEnvelopeForRestaurants(envelope, allDayEnvelope, superLenient = false) {
    if (!superLenient) return envelope;

    const outputRx = { ...envelope };

    delete outputRx['307'];
    delete outputRx['606'];
    delete outputRx.ASG;
    delete outputRx['269'];

    return outputRx;
}

export function nutrNoSortCmp(a, b) {
    const aI = nutrKeys.indexOf(a),
          bI = nutrKeys.indexOf(b);

    if (aI > bI) return 1;
    if (aI < bI) return -1;
    return 0;
}

export function calculateAveragedNutrients(allNutrients, envelope, currentOffset, maxOffset) {
    const averagedNutrients = {};

    Object.keys(envelope).forEach((nutrNo) => {
        const range = envelope[nutrNo];
        const avgPeriod = range.averaging_period;

        if (!avgPeriod || avgPeriod <= 1) {
            // No averaging needed
            averagedNutrients[nutrNo] =
                (allNutrients[currentOffset] &&
                    allNutrients[currentOffset]['all-day'] &&
                    allNutrients[currentOffset]['all-day'][nutrNo]) ||
                0;
            return;
        }

        // Calculate average over specified period
        let total = 0;
        let daysCount = 0;

        for (let i = 0; i < avgPeriod; i++) {
            const offset = currentOffset - i;
            if (offset >= 0 && offset <= maxOffset) {
                if (
                    allNutrients[offset] &&
                    allNutrients[offset]['all-day'] &&
                    typeof allNutrients[offset]['all-day'][nutrNo] !== 'undefined'
                ) {
                    // We have data for this day
                    total += allNutrients[offset]['all-day'][nutrNo];
                } else {
                    // No data for this day - assume minimum value
                    total += range.min || 0;
                }
                daysCount++;
            }
        }

        // Average the total
        averagedNutrients[nutrNo] = daysCount > 0 ? total / daysCount : 0;
    });

    return averagedNutrients;
}

export function compareNutrientsToEnvelope(nutrients, envelope, ignore = []) {
    const deviations = [];

    Object.keys(envelope).forEach(nutrNo => {
        if (ignore.includes(nutrNo)) {
            return;
        }

        let range = envelope[nutrNo];

        let value = (nutrients && nutrients[nutrNo]) || 0;
        value = Math.round(value * 100) / 100;

        // Handle both direct min/max and implicit.min/implicit.max
        let min = range.min;
        let max = range.max;

        // If no direct min/max, check for implicit values
        if (typeof min !== 'number' && range.implicit && typeof range.implicit.min === 'number') {
            min = range.implicit.min;
        }
        if (typeof max !== 'number' && range.implicit && typeof range.implicit.max === 'number') {
            max = range.implicit.max;
        }

        // Check for violations using the resolved min/max values
        if (typeof min === 'number' && value < min) {
            deviations.push({
                ...range,
                nutrNo,
                value,
                min,
                max
            });
        }

        if (typeof max === 'number' && value > max) {
            deviations.push({
                ...range,
                nutrNo,
                value,
                min,
                max
            });
        }
    });

    return deviations;
}

export function computeWeeklyAnalysis(weekStart, profile, allMeals, assets) {
    if (!profile) {
        return {};
    }

    const weekEnd = moment(weekStart).add(6, 'days');
    const demographic = getDemographic(profile);
    const dri = {...allRecommendations['all-ages'], ...allRecommendations[demographic]};
    const meals = allMeals.filter(meal => weekStart.isSameOrBefore(meal.date, 'day') &&
                                          weekEnd.isSameOrAfter(meal.date, 'day'));
    const loggedMeals = meals.filter(meal => meal.logged_portion);
    const nutrients = getNutrientsForMeals(loggedMeals, assets, profile.portion);
    const allDayRx = profile.preferences.daily_totals;
    const rxs = allDayRx ? {'all-day': allDayRx} : {};

    profile.preferences.meal_types.forEach(mealType => {
        const prescription = mealType.envelope;

        if (!prescription) {
            return;
        }

        rxs[mealType.name] = prescription;
    });

    const analysis = {
        vegetables: {
            value: Math.round(nutrients['VEG'] || 0),
            total: ((allDayRx && allDayRx['VEG'] && allDayRx['VEG'].min) || dri['VEG']) * 7,
            percentage: 75,
        },
        fruits: {
            value: Math.round(nutrients['FRU'] || 0),
            total: ((allDayRx && allDayRx['FRU'] && allDayRx['FRU'].min) || dri['FRU']) * 7,
            percentage: 75,
        },
        meals: {
            total: 0,
            within_calories: 0,
            percentage: 0,
            restaurant: 0,
            recipes: 0,
        },
        logging: {
            logged: 0,
            total: meals.length,
            percentage: 0,
        },
    };

    const grouped = {};

    loggedMeals.forEach(meal => {
        const key = meal.meal + '-' + meal.date;

        grouped[key] = grouped[key] || {mealType: meal.meal, date: meal.date, meals: []};
        grouped[key].meals.push(meal);

        if (['fresh', 'leftover'].includes(meal.meal_type)) {
            analysis.meals.recipes++;
        }

        if (['food'].includes(meal.meal_type)) {
            const food = assets[meal.food_uuid];

            if (food?.product_type === 'Restaurant Dish') {
                analysis.meals.restaurant++;
            }
        }
    });

    analysis.logging.logged = Object.keys(grouped).length;
    analysis.vegetables.percentage = analysis.vegetables.total > 0
                               ? Math.round(analysis.vegetables.value / analysis.vegetables.total * 100)
                               : 0;
    analysis.fruits.percentage = analysis.fruits.total > 0
                               ? Math.round(analysis.fruits.value / analysis.fruits.total * 100)
                               : 0;

    analysis.logging.percentage = analysis.logging.total > 0
                                ? Math.round(analysis.logging.logged / analysis.logging.total * 100)
                                : 0;

    Object.keys(grouped).forEach(key => {
        let mealNutes = grouped[key].nutrients = getNutrientsForMeals(grouped[key].meals, assets, profile.portion);

        const mealType = grouped[key].mealType;
        const calories = mealNutes['208'];

        // Is this meal within calorie limits?
        if (rxs[mealType] && rxs[mealType]['208']) {

            const rangeData = rxs[mealType]['208'];

            // Extract min/max from either direct values or implicit values
            const min = rangeData.min ?? rangeData.implicit?.min;
            const max = rangeData.max ?? rangeData.implicit?.max;

            if (min && max && calories >= min && calories <= max) {
                analysis.meals.within_calories++;
            } else if (min && !max && calories >= min) {
                analysis.meals.within_calories++;
            } else if (!min && max && calories <= max) {
                analysis.meals.within_calories++;
            }
        }
    });

    analysis.meals.percentage = analysis.meals.within_calories > 0
                              ? Math.round(analysis.meals.within_calories / analysis.meals.total * 100)
                              : 0;

    return analysis;
}

/**
 * Compare two daily_totals objects to detect if the prescription has changed.
 * Returns true if there are differences, false if they are the same.
 *
 * @param {Object} current - Current daily_totals object
 * @param {Object} resolved - Resolved daily_totals object to compare against
 * @returns {boolean} - True if there are differences greater than 0.01
 */
export function compareDailyTotals(current, resolved) {
    if (!current || !resolved) return false;

    const currentKeys = Object.keys(current);
    const resolvedKeys = Object.keys(resolved);
    const allKeys = [...new Set([...currentKeys, ...resolvedKeys])];

    return allKeys.some(nutrient => {
        const currentValue = current[nutrient];
        const resolvedValue = resolved[nutrient];

        if (!currentValue && resolvedValue) return true;
        if (currentValue && !resolvedValue) return true;
        if (!currentValue && !resolvedValue) return false;

        if (typeof currentValue === 'object' && typeof resolvedValue === 'object') {
            const minDiff = Math.abs((currentValue.min || 0) - (resolvedValue.min || 0));
            const maxDiff = Math.abs((currentValue.max || 0) - (resolvedValue.max || 0));
            return minDiff > 0.01 || maxDiff > 0.01;
        } else if (typeof currentValue === 'number' && typeof resolvedValue === 'number') {
            return Math.abs(currentValue - resolvedValue) > 0.01;
        }

        return true;
    });
}

/**
 * Calculate macro percentages from nutrients
 * Based on MacrosPieChart component logic
 *
 * @param {Object} nutrients - Nutrients object with nutrient numbers as keys
 * @returns {Object} - Object with protein, fat, carb percentages
 */
export function getMacroPercentages(nutrients) {
    if (!nutrients || typeof nutrients !== 'object') {
        return { protein: 0, fat: 0, carb: 0 };
    }

    // Extract macronutrient grams from nutrients object
    // Using standard USDA nutrient numbers
    const proteinGrams = parseFloat(nutrients['203']) || 0;  // Protein
    const fatGrams = parseFloat(nutrients['204']) || 0;      // Total lipid (fat)
    const carbGrams = parseFloat(nutrients['205']) || 0;     // Carbohydrate, by difference

    // Convert grams to calories using standard conversion factors
    const proteinCalories = proteinGrams * 4;  // 4 calories per gram
    const fatCalories = fatGrams * 9;          // 9 calories per gram
    const carbCalories = carbGrams * 4;        // 4 calories per gram

    // Calculate total calories from macros
    const totalCalories = proteinCalories + fatCalories + carbCalories;

    // If no calories, return zero percentages
    if (totalCalories === 0) {
        return { protein: 0, fat: 0, carb: 0 };
    }

    // Calculate percentages and round to nearest integer
    const proteinPercent = Math.round((proteinCalories / totalCalories) * 100);
    const fatPercent = Math.round((fatCalories / totalCalories) * 100);
    const carbPercent = Math.round((carbCalories / totalCalories) * 100);

    return {
        protein: proteinPercent,
        fat: fatPercent,
        carb: carbPercent
    };
}
